import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from './LoadingSpinner';
import { ProductListItem } from '../../types/ProductTypes';
import queryParamsUtils from '../../utils/queryParamsUtils';
import { marpolRegulationToIconMapper, getMarpolRegulationAltText } from '../../utils/iconMapper';
import { useChartererStore, enrichWithChartererNames } from '../../context/ChartererContext';
import {
    ProductsSection,
    ProductsContent,
    ProductsHeader,
    ErrorState,
    EmptyState,
    ProductsTableContainer,
    ProductsTable,
    ProductsTableHeader,
    ProductHeaderCell,
    ProductRow,
    ProductCell,
    PaginationContainer,
    PaginationInfo,
    PaginationControls,
    PaginationButton,
    PageSizeSelect,
} from './ProductsList.styles';

interface ProductsListProps {
    products: ProductListItem[] | null;
    loading: boolean;
    error: string | null;
    title?: string;
    onProductClick?: (productId: number) => void;
    pageSize?: number;
    maxHeight?: string;
}

const ProductsList: React.FC<ProductsListProps> = ({
    products,
    loading,
    error,
    title = 'Products using this procedure',
    onProductClick,
    pageSize: initialPageSize = 20,
    maxHeight,
}) => {
    const navigate = useNavigate();
    const { setQueryParamProductId } = queryParamsUtils();
    const { charterers } = useChartererStore();
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(initialPageSize);

    // Enrich products with charterer names from cache
    const enrichedProducts = useMemo(
        () => products ? enrichWithChartererNames(products, charterers) : null,
        [products, charterers]
    );

    const handleProductClick = (productId: number) => {
        if (onProductClick) {
            onProductClick(productId);
        } else {
            navigate('/product');
            setQueryParamProductId(productId.toString());
        }
    };

    // Calculate pagination
    const totalItems = enrichedProducts?.length || 0;
    const totalPages = Math.ceil(totalItems / pageSize);

    const paginatedProducts = useMemo(() => {
        if (!enrichedProducts) return [];
        const startIndex = (currentPage - 1) * pageSize;
        return enrichedProducts.slice(startIndex, startIndex + pageSize);
    }, [enrichedProducts, currentPage, pageSize]);

    // Reset to first page when products change
    React.useEffect(() => {
        setCurrentPage(1);
    }, [enrichedProducts]);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handlePageSizeChange = (newSize: number) => {
        setPageSize(newSize);
        setCurrentPage(1);
    };

    return (
        <ProductsSection>
            <ProductsContent>
                <ProductsHeader>
                    {title} {totalItems > 0 && <span>({totalItems})</span>}
                </ProductsHeader>

                {loading ? (
                    <LoadingSpinner text="Loading products..." size="small" />
                ) : error ? (
                    <ErrorState>
                        <i className="fas fa-exclamation-triangle"></i>
                        Error loading products: {error}
                    </ErrorState>
                ) : enrichedProducts && enrichedProducts.length > 0 ? (
                    <>
                        <ProductsTableContainer style={maxHeight ? { maxHeight } : undefined}>
                            <ProductsTable>
                                <ProductsTableHeader>
                                    <ProductHeaderCell className="icon-header"></ProductHeaderCell>
                                    <ProductHeaderCell>Trade Name</ProductHeaderCell>
                                    <ProductHeaderCell>Product Name (IBC)</ProductHeaderCell>
                                    <ProductHeaderCell>Charterer</ProductHeaderCell>
                                    <ProductHeaderCell>CAS Number</ProductHeaderCell>
                                </ProductsTableHeader>

                                {paginatedProducts.map((product) => (
                                    <ProductRow key={product.id} onClick={() => handleProductClick(product.id)}>
                                        <ProductCell className="icon-cell">
                                            <img
                                                src={marpolRegulationToIconMapper(Number(product.marpolRegulation) || 0)}
                                                alt={getMarpolRegulationAltText(Number(product.marpolRegulation) || 0)}
                                                className="marpol-icon"
                                            />
                                        </ProductCell>
                                        <ProductCell className="product-name-cell">
                                            <span className="product-name">{product.tradeName || 'Unknown Product'}</span>
                                        </ProductCell>
                                        <ProductCell>{product.categoryName || 'Unknown'}</ProductCell>
                                        <ProductCell>{product.chartererName || '-'}</ProductCell>
                                        <ProductCell>{product.casNumber || '-'}</ProductCell>
                                    </ProductRow>
                                ))}
                            </ProductsTable>
                        </ProductsTableContainer>

                        {totalPages > 1 && (
                            <PaginationContainer>
                                <PaginationInfo>
                                    Showing {(currentPage - 1) * pageSize + 1}-{Math.min(currentPage * pageSize, totalItems)} of {totalItems}
                                </PaginationInfo>
                                <PaginationControls>
                                    <PaginationButton onClick={() => handlePageChange(1)} disabled={currentPage === 1} title="First page">
                                        <i className="fas fa-angle-double-left"></i>
                                    </PaginationButton>
                                    <PaginationButton
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage === 1}
                                        title="Previous page"
                                    >
                                        <i className="fas fa-angle-left"></i>
                                    </PaginationButton>
                                    <span>
                                        Page {currentPage} of {totalPages}
                                    </span>
                                    <PaginationButton
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                        title="Next page"
                                    >
                                        <i className="fas fa-angle-right"></i>
                                    </PaginationButton>
                                    <PaginationButton
                                        onClick={() => handlePageChange(totalPages)}
                                        disabled={currentPage === totalPages}
                                        title="Last page"
                                    >
                                        <i className="fas fa-angle-double-right"></i>
                                    </PaginationButton>
                                </PaginationControls>
                                <PageSizeSelect>
                                    <label htmlFor="page-size-select">Per page:</label>
                                    <select
                                        id="page-size-select"
                                        title="Items per page"
                                        value={pageSize}
                                        onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                                    >
                                        <option value={10}>10</option>
                                        <option value={20}>20</option>
                                        <option value={50}>50</option>
                                        <option value={100}>100</option>
                                    </select>
                                </PageSizeSelect>
                            </PaginationContainer>
                        )}
                    </>
                ) : (
                    <EmptyState>No products found using this procedure.</EmptyState>
                )}
            </ProductsContent>
        </ProductsSection>
    );
};

export default ProductsList;
